import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Loader2, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ReviewImagesField } from '@/components/campaigns/ReviewImagesField';
import { ADMIN_BULK_DISAPPROVE_CPM_CAMPAIGN_SUBMISSIONS } from '@/queries/queries';
import {
  DISAPPROVE_REASON_CHIPS,
  resolveDisapproveReason,
} from '@/lib/campaigns/resolveDisapproveReason';
import { mapBulkDisapproveError } from '@/lib/campaigns/mapBulkDisapproveError';
import type { ReviewImage } from '@/lib/campaigns/reviewImages';

type DisapproveChip = typeof DISAPPROVE_REASON_CHIPS[number];

export interface BulkDisapproveResult {
  disapproved: number;
  skipped: Array<{ id: string; code: string }>;
}

interface BulkDisapproveDialogProps {
  open: boolean;
  /** Selected pending submissions (documentIds). */
  submissionIds: string[];
  onOpenChange: (open: boolean) => void;
  onDisapproved: (result: BulkDisapproveResult) => void;
}

const PREFIX = 'admin.campaigns.submissions';

/**
 * Rejects every selected pending clip with one reason (a preset chip or the
 * reviewer's own text) and the same optional images, in one request. A refused
 * request keeps the draft and explains why, so the reviewer can retry.
 */
export function BulkDisapproveDialog({
  open,
  submissionIds,
  onOpenChange,
  onDisapproved,
}: BulkDisapproveDialogProps) {
  const { t } = useTranslation();
  const [chip, setChip] = useState<DisapproveChip | null>(null);
  const [otherText, setOtherText] = useState('');
  const [images, setImages] = useState<ReviewImage[]>([]);
  const [imagesBusy, setImagesBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [disapprove, { loading }] = useMutation(ADMIN_BULK_DISAPPROVE_CPM_CAMPAIGN_SUBMISSIONS);

  const count = submissionIds.length;
  const resolved = resolveDisapproveReason({ chip, otherText });
  const canConfirm = resolved.ok && count > 0 && !imagesBusy && !loading;

  const handleConfirm = async () => {
    if (!resolved.ok || count === 0) return;
    setErrorKey(null);
    try {
      const result = await disapprove({
        variables: { ids: submissionIds, reason: resolved.reason, images },
      });
      const payload = result.data?.adminBulkDisapproveCpmCampaignSubmissions;
      onDisapproved({
        disapproved: payload?.disapproved ?? 0,
        skipped: payload?.skipped ?? [],
      });
    } catch (err) {
      setErrorKey(mapBulkDisapproveError(err as Parameters<typeof mapBulkDisapproveError>[0]));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing mid-request would hide the result of a write still in flight.
        if (!next && loading) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`${PREFIX}.bulkDisapprove.title`, { count })}</DialogTitle>
          <DialogDescription>
            {t(`${PREFIX}.bulkDisapprove.description`, { count })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{t(`${PREFIX}.reasonLabel`)}</Label>
            <div className="flex flex-wrap gap-1">
              {DISAPPROVE_REASON_CHIPS.map((option) => (
                <Button
                  key={option}
                  type="button"
                  variant={chip === option ? 'destructive' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  aria-pressed={chip === option}
                  onClick={() => {
                    setChip(option);
                    if (option !== 'other') setOtherText('');
                  }}
                >
                  {t(`${PREFIX}.reasonChips.${option}`)}
                </Button>
              ))}
            </div>
            {chip === 'other' && (
              <Textarea
                value={otherText}
                onChange={(event) => setOtherText(event.target.value)}
                placeholder={t(`${PREFIX}.reasonPlaceholder`)}
                aria-label={t(`${PREFIX}.reasonLabel`)}
                maxLength={500}
                rows={3}
                autoFocus
              />
            )}
            <p className="text-xs text-muted-foreground">
              {t(`${PREFIX}.bulkDisapprove.reasonHint`)}
            </p>
          </div>

          <ReviewImagesField
            value={images}
            onChange={setImages}
            disabled={loading}
            onBusyChange={setImagesBusy}
          />

          {errorKey && (
            <p role="alert" className="text-sm text-destructive">
              {t(errorKey)}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={loading}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!canConfirm}
            onClick={() => { void handleConfirm(); }}
          >
            {loading
              ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              : <ThumbsDown className="mr-1 h-4 w-4" />}
            {t(`${PREFIX}.bulkDisapprove.confirm`, { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
